import type { TFunction } from 'i18next';

import { getConsignmentsStatusOptions, getDateRangeOptions } from '@/features/dashboard-filters';
import type { ActiveFilterChip } from '@/features/header-filters';
import type { SelectOption } from '@/shared/ui';

import type { ConsignmentsUrlState } from '../model/types';
import { DEFAULT_URL_STATE } from './constants';

interface BuildConsignmentsFilterChipsArgs {
  state: ConsignmentsUrlState;
  t: TFunction;
  // id → name options for the organization filter
  organizationOptions: SelectOption[];
  onRemove: (key: string) => void;
}

/**
 * Derives the removable active-filter chips from the committed consignments
 * filter state. Mirrors the `activeFiltersCount` accounting in
 * use-consignments-filters (pagination/sort skipped, the date range counted
 * as a single chip) so the chip count matches the header badge. `allSites` is
 * excluded on both sides — it is the header viewpoint switch, surfaced by the
 * page title rather than by a chip.
 */
export function buildConsignmentsFilterChips({
  state,
  t,
  organizationOptions,
  onRemove
}: BuildConsignmentsFilterChipsArgs): ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = [];

  if (state.status && state.status !== DEFAULT_URL_STATE.status) {
    const label = getConsignmentsStatusOptions(t).find((o) => o.value === state.status)?.label ?? state.status;
    chips.push({ key: 'status', label, onRemove: () => onRemove('status') });
  }

  // Date range — period + creationDateFrom/To collapse into one chip
  const hasCustomDates = Boolean(state.creationDateFrom || state.creationDateTo);
  const hasPeriod = Boolean(state.period && state.period !== DEFAULT_URL_STATE.period);
  if (hasCustomDates || hasPeriod) {
    const label = hasCustomDates
      ? [state.creationDateFrom, state.creationDateTo].filter(Boolean).join(' – ')
      : (getDateRangeOptions(t).find((o) => o.value === state.period)?.label ?? state.period ?? '');
    chips.push({ key: 'date', label, onRemove: () => onRemove('date') });
  }

  if (state.requestNumber) {
    chips.push({ key: 'requestNumber', label: state.requestNumber, onRemove: () => onRemove('requestNumber') });
  }

  if (state.orgId) {
    const label = organizationOptions.find((o) => o.value === state.orgId)?.label ?? state.orgId;
    chips.push({ key: 'orgId', label, onRemove: () => onRemove('orgId') });
  }

  if (state.waybill) {
    chips.push({ key: 'waybill', label: state.waybill, onRemove: () => onRemove('waybill') });
  }

  return chips;
}
